import { useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router';

import { useCloseMenu } from '@stores/index';
import { scrollManager } from '@systems/ScrollManager';

/** The id of the element that receives focus after navigation. */
export const MAIN_CONTENT_ID = 'main';

/**
 * Housekeeping on every route change: close the mobile menu, then either
 * scroll to the `#hash` target or reset to the top and move focus to the
 * new page's content.
 *
 * Must render BEFORE <Outlet /> in the layout. Layout effects run in tree
 * order, so this resets scroll before the new page creates its reveal
 * triggers, which would otherwise measure against the old scroll position
 * and fire all at once. The new page's DOM is already committed by then,
 * so a hash target can be found.
 *
 * Focus moves so screen-reader and keyboard users land on the new content
 * rather than staying on a link in a header that did not change. Skipped on
 * the initial load, where the browser already starts at the top.
 */
export const useRouteChange = (): void => {
  const { pathname, hash } = useLocation();
  const closeMenu = useCloseMenu();
  /* The location this effect last handled. Compared rather than using a
     "first render" flag, because StrictMode replays effects in development
     and a flag would mistake the replay for a navigation. */
  const handled = useRef<string | null>(null);

  useLayoutEffect(() => {
    const location = pathname + hash;
    if (handled.current === location) return;
    const isInitial = handled.current === null;
    handled.current = location;
    if (!isInitial) closeMenu();

    if (hash) {
      const target = document.getElementById(decodeURIComponent(hash.slice(1)));
      if (target) scrollManager.scrollToElement(target, { immediate: isInitial });
      return;
    }

    if (isInitial) return;
    scrollManager.resetToTop();
    document.getElementById(MAIN_CONTENT_ID)?.focus({ preventScroll: true });
  }, [pathname, hash, closeMenu]);
};
